import { useEffect } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';

import { SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT } from '../analytics.ts';
import { useShowSuggestionsPanel } from '../useShowSuggestionsPanel.ts';

import {
	getPublishedSuggestionsSnapshot,
	invalidatePublishedSuggestionsCache,
	resetPublishedSuggestions,
} from './publishedSuggestionsStore.ts';
import { usePublishedSuggestionsState } from './usePublishedSuggestionsState.ts';

export const useSuggestionsPanelEditHandoff = (contentId: string | undefined): boolean => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const { isSuggestionsPanelShown } = useShowSuggestionsPanel();
	const { contentId: publishedContentId } = usePublishedSuggestionsState();

	const isPublishedMode =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The suggested edits experiment is the kill switch for the view mode Suggestions panel subgate.
		publishedContentId !== undefined &&
		expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
		fg('confluence_suggestions_panel_view_mode');
	const isEditHandoffPending =
		isPublishedMode && isSuggestionsPanelShown && !!contentId && publishedContentId === contentId;

	useEffect(() => {
		if (
			!contentId ||
			!expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
			!fg('confluence_suggestions_panel_view_mode')
		) {
			return;
		}
		invalidatePublishedSuggestionsCache(contentId);
		return () => invalidatePublishedSuggestionsCache(contentId);
	}, [contentId]);

	useEffect(() => {
		if (isPublishedMode && !isEditHandoffPending) {
			resetPublishedSuggestions();
		}
	}, [isEditHandoffPending, isPublishedMode]);

	useEffect(() => {
		const aiSuggestions = fullPageEditorPresetAPI?.aiSuggestions;
		if (!isEditHandoffPending || !aiSuggestions) {
			return;
		}

		const { collabEdit } = fullPageEditorPresetAPI;
		let isCancelled = false;
		let hasStarted = false;
		let unsubscribe: (() => void) | undefined;

		const completeHandoff = async () => {
			if (hasStarted) {
				return;
			}
			hasStarted = true;
			unsubscribe?.();

			try {
				await aiSuggestions.actions.refreshSuggestions();
			} catch {}

			if (isCancelled) {
				return;
			}

			const { selectedSuggestionId, shouldStartReviewInEditMode } =
				getPublishedSuggestionsSnapshot();
			if (
				selectedSuggestionId &&
				aiSuggestions.sharedState.currentState()?.suggestionById.has(selectedSuggestionId)
			) {
				aiSuggestions.actions.selectSuggestion(selectedSuggestionId, {
					interactionPoint: 'sidebar',
				});
			}

			if (shouldStartReviewInEditMode) {
				aiSuggestions.actions.setThinkingState('thinking', {
					entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
				});
			}

			resetPublishedSuggestions();
		};

		if (!collabEdit || collabEdit.sharedState.currentState()?.initialised.collabInitialisedAt) {
			void completeHandoff();
		} else {
			unsubscribe = collabEdit.sharedState.onChange(({ nextSharedState }) => {
				if (nextSharedState?.initialised.collabInitialisedAt) {
					void completeHandoff();
				}
			});
		}

		return () => {
			isCancelled = true;
			unsubscribe?.();
		};
	}, [fullPageEditorPresetAPI, isEditHandoffPending]);

	return isEditHandoffPending;
};
